import React, { useState, useMemo, useCallback } from 'react';
import {
  Box,
  Alert,
  IconButton,
  useTheme,
  alpha,
  Card,
  CardContent,
} from '@mui/material';
import {
  Cancel,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import api, { downtrendAPI } from '../services/api';
import { format, parseISO, subDays } from 'date-fns';
import ControlPanel from '../components/downtrend/ControlPanel';
import DowntrendHeader from '../components/downtrend/DowntrendHeader';
import BatchResultsSection from '../components/downtrend/BatchResultsSection';
import AnalysisContent from '../components/downtrend/AnalysisContent';
import DowntrendEmptyState from '../components/downtrend/DowntrendEmptyState';
import IndicatorsDialog from '../components/downtrend/IndicatorsDialog';
import { getPhaseColor, getRiskColor, getRiskLabel } from '../components/downtrend/utils';

function DowntrendDetection() {
  const theme = useTheme();
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  const [symbol, setSymbol] = useState('VN30');
  const [startDate, setStartDate] = useState(
    format(subDays(new Date(), 120), 'yyyy-MM-dd')
  );
  const [endDate, setEndDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [lookbackDays, setLookbackDays] = useState(120);
  const [activeTab, setActiveTab] = useState(0);
  const [expandedSections, setExpandedSections] = useState({
    signals: true,
    metrics: true,
    chart: true,
    alerts: true,
  });
  const [indicatorsDialogOpen, setIndicatorsDialogOpen] = useState(false);
  const [batchMode, setBatchMode] = useState(false);
  const [batchSymbols, setBatchSymbols] = useState([]);

  // Fetch alerts
  const { data: alertsData, isLoading: alertsLoading } = useQuery({
    queryKey: ['downtrend-alerts'],
    queryFn: () => downtrendAPI.getAlerts(),
    refetchInterval: 30000,
  });

  // Fetch indicators
  const { data: indicatorsData } = useQuery({
    queryKey: ['downtrend-indicators'],
    queryFn: () => downtrendAPI.getIndicators(),
  });

  // Detection mutation
  const detectMutation = useMutation({
    mutationFn: (params) =>
      downtrendAPI.detect(
        params.symbol,
        params.startDate,
        params.endDate,
        params.lookbackDays
      ),
    onSuccess: () => {
      queryClient.invalidateQueries(['downtrend-alerts']);
    },
  });

  // Batch detection mutation
  const batchDetectMutation = useMutation({
    mutationFn: (requests) => api.post('/downtrend/batch-detect', requests),
    onSuccess: () => {
      queryClient.invalidateQueries(['downtrend-alerts']);
    },
  });

  const handleDetect = useCallback(() => {
    detectMutation.mutate({ symbol, startDate, endDate, lookbackDays });
  }, [symbol, startDate, endDate, lookbackDays, detectMutation]);

  const handleBatchDetect = useCallback(() => {
    if (batchSymbols.length === 0) return;
    const requests = batchSymbols.map((sym) => ({
      symbol: sym,
      start_date: startDate || null,
      end_date: endDate || null,
      lookback_days: lookbackDays,
    }));
    batchDetectMutation.mutate(requests);
  }, [batchSymbols, startDate, endDate, lookbackDays, batchDetectMutation]);

  const detectionData = detectMutation.data?.data;
  const alerts = alertsData?.data?.alerts || [];
  const alertCount = alerts.length;
  const indicators = indicatorsData?.data?.indicators || [];

  const chartData = useMemo(() => {
    if (!detectionData?.results || !detectionData?.dates) return [];

    return detectionData.results.map((result, idx) => ({
      date: detectionData.dates[idx]
        ? format(parseISO(detectionData.dates[idx]), 'MMM dd')
        : `Day ${idx + 1}`,
      dateFull: detectionData.dates[idx] || '',
      risk10: (result.down_risk_10 || 0) * 100,
      risk20: (result.down_risk_20 || 0) * 100,
      risk60: (result.down_risk_60 || 0) * 100,
      techRisk: (result.tech_risk || 0) * 100,
      macroRisk: (result.macro_risk || 0) * 100,
      phase10: result.down_phase_10,
      phase20: result.down_phase_20,
      phase60: result.down_phase_60,
    }));
  }, [detectionData]);

  const latestResult = detectionData?.results?.[detectionData.results.length - 1];
  const latestPhase = detectionData?.phase;
  const phaseInfo = useMemo(() => getPhaseColor(latestPhase), [latestPhase]);

  const riskSnapshot = useMemo(() => {
    const overallRisk = latestResult?.down_risk_60 !== null && latestResult?.down_risk_60 !== undefined
      ? latestResult.down_risk_60 * 100
      : null;
    const techRisk = detectionData?.risk_score ?? null;
    const macroRisk = detectionData?.macro_score ?? null;
    const horizons = [
      {
        key: '10',
        label: t('downtrend.riskHorizons.horizon10', 'Tầm nhìn 10 ngày'),
        phase: latestResult?.down_phase_10,
        risk: latestResult?.down_risk_10 !== null && latestResult?.down_risk_10 !== undefined ? latestResult.down_risk_10 * 100 : null,
      },
      {
        key: '20',
        label: t('downtrend.riskHorizons.horizon20', 'Tầm nhìn 20 ngày'),
        phase: latestResult?.down_phase_20,
        risk: latestResult?.down_risk_20 !== null && latestResult?.down_risk_20 !== undefined ? latestResult.down_risk_20 * 100 : null,
      },
      {
        key: '60',
        label: t('downtrend.riskHorizons.horizon60', 'Tầm nhìn 60 ngày'),
        phase: latestResult?.down_phase_60,
        risk: latestResult?.down_risk_60 !== null && latestResult?.down_risk_60 !== undefined ? latestResult.down_risk_60 * 100 : null,
      },
    ];
    const dominantHorizon = horizons
      .filter((item) => item.risk !== null)
      .sort((a, b) => (b.risk || 0) - (a.risk || 0))[0] || null;

    return {
      overallRisk,
      techRisk,
      macroRisk,
      horizons,
      dominantHorizon,
      technicalSignals: detectionData?.technical_signals?.length || 0,
      macroSignals: detectionData?.macro_signals?.length || 0,
      alertsGenerated: detectionData?.alerts_generated || 0,
      dataPoints: detectionData?.results?.length || 0,
      priceChange30d: detectionData?.price_change_30d,
      volatility30d: detectionData?.volatility_30d,
      relativeStrength: detectionData?.relative_strength,
    };
  }, [detectionData, latestResult, t]);

  const overviewNarrative = useMemo(() => {
    if (!detectionData || !latestResult) return null;

    if (latestPhase === 'SEVERE_DOWN') {
      return {
        eyebrow: t('downtrend.overview.alertTitle', 'Cần phòng thủ ngay'),
        title: t('downtrend.overview.severeTitle', 'Xu hướng giảm đã vào pha nguy hiểm'),
        body: t('downtrend.overview.severeBody', 'Ưu tiên bảo toàn vốn, giảm kỳ vọng bắt đáy và chỉ theo dõi phản ứng ở các vùng hỗ trợ thực sự mạnh.'),
      };
    }
    if (latestPhase === 'CONFIRMED_DOWN') {
      return {
        eyebrow: t('downtrend.overview.alertTitle', 'Rủi ro đã xác nhận'),
        title: t('downtrend.overview.confirmedTitle', 'Áp lực giảm đã rõ, chưa phải vùng giải ngân thoải mái'),
        body: t('downtrend.overview.confirmedBody', 'Tập trung theo dõi độ dốc rủi ro và phản ứng hồi kỹ thuật thay vì nhìn mỗi giá giảm hay tăng trong 1-2 phiên.'),
      };
    }
    if (latestPhase === 'EARLY_WARNING') {
      return {
        eyebrow: t('downtrend.overview.alertTitle', 'Có dấu hiệu sớm'),
        title: t('downtrend.overview.warningTitle', 'Xu hướng vẫn giữ được, nhưng rủi ro đang nhích lên'),
        body: t('downtrend.overview.warningBody', 'Đây là vùng cần quan sát kỹ khung 10-20 ngày để phát hiện sớm khi áp lực bán lan rộng hơn.'),
      };
    }
    return {
      eyebrow: t('downtrend.overview.alertTitle', 'Bối cảnh hiện tại'),
      title: t('downtrend.overview.neutralTitle', 'Xu hướng chưa vào pha giảm đáng lo'),
      body: t('downtrend.overview.neutralBody', 'Có thể dùng dashboard này như bảng theo dõi sớm để xem khi nào rủi ro kỹ thuật hoặc vĩ mô bắt đầu chuyển xấu.'),
    };
  }, [detectionData, latestPhase, latestResult, t]);

  const quickFacts = useMemo(() => {
    if (!detectionData || !latestResult) return [];

    return [
      {
        label: t('downtrend.overview.factPrimaryRisk', 'Khung áp lực lớn nhất'),
        value: riskSnapshot.dominantHorizon
          ? `${riskSnapshot.dominantHorizon.label} • ${riskSnapshot.dominantHorizon.risk.toFixed(1)}%`
          : t('downtrend.riskLabels.na'),
        tone: riskSnapshot.dominantHorizon ? getRiskColor(riskSnapshot.dominantHorizon.risk) : '#94a3b8',
      },
      {
        label: t('downtrend.overview.factTech', 'Rủi ro kỹ thuật'),
        value: riskSnapshot.techRisk !== null && riskSnapshot.techRisk !== undefined
          ? `${riskSnapshot.techRisk.toFixed(1)}% • ${getRiskLabel(riskSnapshot.techRisk, t)}`
          : t('downtrend.riskLabels.na'),
        tone: getRiskColor(riskSnapshot.techRisk),
      },
      {
        label: t('downtrend.overview.factMacro', 'Rủi ro vĩ mô'),
        value: riskSnapshot.macroRisk !== null && riskSnapshot.macroRisk !== undefined
          ? `${riskSnapshot.macroRisk.toFixed(1)}% • ${getRiskLabel(riskSnapshot.macroRisk, t)}`
          : t('downtrend.riskLabels.na'),
        tone: getRiskColor(riskSnapshot.macroRisk),
      },
      {
        label: t('downtrend.overview.factSignals', 'Tín hiệu đang bật'),
        value: `${riskSnapshot.technicalSignals + riskSnapshot.macroSignals} • ${riskSnapshot.technicalSignals} tech / ${riskSnapshot.macroSignals} vĩ mô`,
        tone: phaseInfo.color,
      },
    ];
  }, [detectionData, latestResult, phaseInfo.color, riskSnapshot, t]);

  const toggleSection = (section) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const quickDatePresets = [
    { days: 30 },
    { days: 60 },
    { days: 90 },
    { days: 120 },
    { days: 180 },
  ];

  const applyDatePreset = (days) => {
    setStartDate(format(subDays(new Date(), days), 'yyyy-MM-dd'));
    setEndDate(format(new Date(), 'yyyy-MM-dd'));
    setLookbackDays(days);
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(180deg, #f8fbff 0%, #ffffff 22%, #f8fafc 100%)',
        px: { xs: 1.5, sm: 2.25, md: 3 },
        py: { xs: 1.5, md: 2 },
      }}
    >
      <Box sx={{ width: '100%', maxWidth: 1440, mx: 'auto' }}>
        <DowntrendHeader
          alertCount={alertCount}
          onAlertsClick={() => setActiveTab(3)}
          onOpenIndicators={() => setIndicatorsDialogOpen(true)}
        />

        {/* Control Panel */}
        <Card
          elevation={0}
          sx={{
            mb: 2.25,
            borderRadius: 3,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.08)}`,
            background: 'linear-gradient(135deg, rgba(255,255,255,0.98) 0%, rgba(248,250,252,0.98) 100%)',
            boxShadow: '0 10px 28px rgba(15, 23, 42, 0.05)',
            overflow: 'hidden',
          }}
        >
          <CardContent sx={{ p: { xs: 1.5, md: 2 } }}>
            <ControlPanel
              batchMode={batchMode}
              setBatchMode={setBatchMode}
              symbol={symbol}
              setSymbol={setSymbol}
              startDate={startDate}
              setStartDate={setStartDate}
              endDate={endDate}
              setEndDate={setEndDate}
              lookbackDays={lookbackDays}
              setLookbackDays={setLookbackDays}
              batchSymbols={batchSymbols}
              setBatchSymbols={setBatchSymbols}
              handleDetect={handleDetect}
              handleBatchDetect={handleBatchDetect}
              detectMutation={detectMutation}
              batchDetectMutation={batchDetectMutation}
              quickDatePresets={quickDatePresets}
              applyDatePreset={applyDatePreset}
            />
          </CardContent>
        </Card>

        {/* Error Alert */}
        {detectMutation.isError && (
          <Alert
            severity="error"
            sx={{ mb: 3 }}
            onClose={() => detectMutation.reset()}
            action={
              <IconButton
                size="small"
                onClick={() => detectMutation.reset()}
                color="inherit"
              >
                <Cancel />
              </IconButton>
            }
          >
            {detectMutation.error?.response?.data?.detail ||
              t('downtrend.errors.detectionFailed')}
          </Alert>
        )}

        {batchDetectMutation.data?.data?.batch_results?.length > 0 && (
          <BatchResultsSection batchResults={batchDetectMutation.data.data.batch_results} />
        )}

        {detectionData && (
          <AnalysisContent
            symbol={symbol}
            lookbackDays={lookbackDays}
            alertCount={alertCount}
            activeTab={activeTab}
            onTabChange={(event, newValue) => setActiveTab(newValue)}
            overviewNarrative={overviewNarrative}
            phaseInfo={phaseInfo}
            quickFacts={quickFacts}
            riskSnapshot={riskSnapshot}
            expandedSections={expandedSections}
            toggleSection={toggleSection}
            latestPhase={latestPhase}
            latestResult={latestResult}
            detectionData={detectionData}
            chartData={chartData}
            alerts={alerts}
            alertsLoading={alertsLoading}
            onRefreshAlerts={() => queryClient.invalidateQueries(['downtrend-alerts'])}
          />
        )}

        {!detectionData && !detectMutation.isLoading && (
          <DowntrendEmptyState
            onOpenIndicators={() => setIndicatorsDialogOpen(true)}
            onEnableBatchMode={() => setBatchMode(true)}
          />
        )}

        <IndicatorsDialog
          open={indicatorsDialogOpen}
          onClose={() => setIndicatorsDialogOpen(false)}
          indicators={indicators}
        />
      </Box>
    </Box>
  );
}

export default DowntrendDetection;
